import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';

type ToastType = 'info' | 'success' | 'error';
type Toast = { id: string; message: string; type: ToastType; duration?: number };

type ConfirmOptions = {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  danger?: boolean;
};

type PromptOptions = {
  title?: string;
  message?: string;
  placeholder?: string;
  defaultValue?: string;
  confirmText?: string;
  cancelText?: string;
  inputLabel?: string;
};

type UiContextValue = {
  toast: (message: string, type?: ToastType, duration?: number) => void;
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  prompt: (options: PromptOptions) => Promise<string | null>;
};

const UiContext = createContext<UiContextValue | null>(null);

export function useUi() {
  const ctx = useContext(UiContext);
  if (!ctx) throw new Error('useUi must be used within UiProvider');
  return ctx;
}

export const UiProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastTimeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const location = useLocation();

  // Clear all toasts when navigating to a different route
  useEffect(() => {
    // Clear all timeouts
    toastTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
    toastTimeoutsRef.current.clear();
    // Clear all toasts
    setToasts([]);
  }, [location.pathname]);

  const [confirmState, setConfirmState] = useState<(ConfirmOptions & { open: boolean }) | null>(null);
  const confirmResolveRef = useRef<((value: boolean) => void) | null>(null);

  const [promptState, setPromptState] = useState<(PromptOptions & { open: boolean }) | null>(null);
  const [promptValue, setPromptValue] = useState('');
  const promptResolveRef = useRef<((value: string | null) => void) | null>(null);

  const dismissToast = useCallback((id: string) => {
    // Clear timeout if exists
    const timeout = toastTimeoutsRef.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      toastTimeoutsRef.current.delete(id);
    }
    // Remove toast
    setToasts((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const toast = useCallback((message: string, type: ToastType = 'info', duration: number = 2000) => {
    const id = `${Date.now()}_${Math.random().toString(16).slice(2)}`;
    const t: Toast = { id, message, type, duration };
    setToasts((prev) => [...prev, t]);
    
    // Set timeout to auto-dismiss
    const timeout = setTimeout(() => {
      setToasts((prev) => prev.filter((x) => x.id !== id));
      toastTimeoutsRef.current.delete(id);
    }, duration);
    
    toastTimeoutsRef.current.set(id, timeout);
  }, []);

  const confirm = useCallback((options: ConfirmOptions) => {
    setConfirmState({ ...options, open: true });
    return new Promise<boolean>((resolve) => {
      confirmResolveRef.current = resolve;
    });
  }, []);

  const prompt = useCallback((options: PromptOptions) => {
    setPromptValue(options.defaultValue || '');
    setPromptState({ ...options, open: true });
    return new Promise<string | null>((resolve) => {
      promptResolveRef.current = resolve;
    });
  }, []);

  const value = useMemo<UiContextValue>(() => ({ toast, confirm, prompt }), [toast, confirm, prompt]);

  return (
    <UiContext.Provider value={value}>
      {children}

      {/* Toasts */}
      {toasts.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] space-y-2">
          {toasts.map((t) => (
            <div
              key={t.id}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg shadow-lg text-sm text-white min-w-[300px] max-w-[500px] ${
                t.type === 'success'
                  ? 'bg-[var(--color-brand-primary)]'
                  : t.type === 'error'
                    ? 'bg-red-600'
                    : 'bg-[var(--color-bg-elevated)] border border-[var(--color-border)]'
              }`}
            >
              <span className="flex-1">{t.message}</span>
              <button
                onClick={() => dismissToast(t.id)}
                className="flex-shrink-0 p-0.5 rounded hover:bg-black/20 transition-colors"
                aria-label="Dismiss"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Confirm modal */}
      {confirmState?.open && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-lg bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-700">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="text-base font-semibold text-gray-900 dark:text-white">
                {confirmState.title || 'Confirm'}
              </div>
            </div>
            <div className="px-5 py-4 text-sm text-gray-700 dark:text-gray-200 whitespace-pre-line">
              {confirmState.message}
            </div>
            <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                onClick={() => {
                  setConfirmState(null);
                  confirmResolveRef.current?.(false);
                  confirmResolveRef.current = null;
                }}
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                {confirmState.cancelText || 'Cancel'}
              </button>
              <button
                onClick={() => {
                  setConfirmState(null);
                  confirmResolveRef.current?.(true);
                  confirmResolveRef.current = null;
                }}
                className={`px-3 py-2 rounded-lg text-white ${
                  confirmState.danger ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {confirmState.confirmText || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Prompt modal */}
      {promptState?.open && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-lg bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-700">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="text-base font-semibold text-gray-900 dark:text-white">
                {promptState.title || 'Enter value'}
              </div>
            </div>
            <div className="px-5 py-4 space-y-3">
              {promptState.message && (
                <div className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-line">{promptState.message}</div>
              )}
              <div>
                {promptState.inputLabel && (
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">{promptState.inputLabel}</label>
                )}
                <input
                  value={promptValue}
                  onChange={(e) => setPromptValue(e.target.value)}
                  placeholder={promptState.placeholder || ''}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                  autoFocus
                />
              </div>
            </div>
            <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                onClick={() => {
                  setPromptState(null);
                  promptResolveRef.current?.(null);
                  promptResolveRef.current = null;
                }}
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                {promptState.cancelText || 'Cancel'}
              </button>
              <button
                onClick={() => {
                  const v = promptValue.trim();
                  setPromptState(null);
                  promptResolveRef.current?.(v ? v : null);
                  promptResolveRef.current = null;
                }}
                className="px-3 py-2 rounded-lg text-white bg-blue-600 hover:bg-blue-700"
              >
                {promptState.confirmText || 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </UiContext.Provider>
  );
};
